import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ScrollReveal from '../../../../components/ScrollReveal';
import heroBgImage from '../../../../images/services/vapt/advanced-testing-hero.webp';
import overviewImage from '../../../../images/services/vapt/vapt-image.webp';
import ctaBgImage from '../../../../images/cta/cta-background.webp';

import { Search,
  Target,
  ScanLine,
  CheckSquare,
  Shield,
  AlertTriangle,
  FileText,
  RefreshCw,
  Activity,
  Award,
  Users,
  Headphones,
  ArrowRight,
  CheckCircle, Landmark } from 'lucide-react';
import './VulnerabilityAssessment.css';

/**
 * Vulnerability Assessment Service Page
 * Identifying and Strengthening Security Weaknesses
 */

const VulnerabilityAssessment = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Approach Methodology Data
  const approachSteps = [
    {
      title: 'Asset Identification',
      description: 'Define the scope of digital assets to be assessed: applications, servers, and network elements.',
      icon: <Search size={30} />
    },
    {
      title: 'Threat Profiling',
      description: 'Map potential attack vectors and well documented exposure to identify cyber threats.',
      icon: <Target size={30} />
    },
    {
      title: 'Scanning & Enumeration',
      description: 'Use automated tools to detect known vulnerabilities and misconfigured systems.',
      icon: <ScanLine size={30} />
    },
    {
      title: 'Manual Validation',
      description: 'Validate findings to eliminate false positives and uncover nuances.',
      icon: <CheckSquare size={30} />
    },
    {
      title: 'Risk Classification',
      description: 'Categorize vulnerabilities based on CVSS scores and business impact.',
      icon: <Shield size={30} />
    },
    {
      title: 'Remediation Guidance',
      description: 'Provide clear, actionable recommendations for fixing identified weaknesses.',
      icon: <AlertTriangle size={30} />
    },
    {
      title: 'Reporting & Insights',
      description: 'Deliver detailed reports highlighting severity, risk, and remediation timelines.',
      icon: <FileText size={30} />
    },
    {
      title: 'Re Assessment',
      description: 'Verify closure of vulnerabilities through re testing post mitigation.',
      icon: <RefreshCw size={30} />
    },
    {
      title: 'Continuous Monitoring',
      description: 'Implement scheduled scans and dashboards to maintain ongoing visibility and improve security.',
      icon: <Activity size={30} />
    }
  ];

  // Why Choose Data
  const whyChooseItems = [
    {
      title: 'Partnered with CERT In Empanelled Experts',
      description: 'Recognized by the Government of India for security audits.',
      icon: <Award size={30} strokeWidth={1.75} />
    },
    {
      title: 'Proven Expertise',
      description: '350+ clients successfully audited across banking, IT, insurance, healthcare, and manufacturing sectors.',
      icon: <Users size={30} strokeWidth={1.75} />
    },
    {
      title: 'End to End Support',
      description: 'From scoping to remediation and final certification with dedicated advisor support.',
      icon: <Headphones size={30} strokeWidth={1.75} />
    }
  ];

  // Difference section content
  const difference = {
    traditional: [
      'Relies solely on automated scanning tools without manual verification.',
      'Generates high volumes of false positives requiring extensive triage.',
      'Provides generic recommendations without context specific guidance.',
      'Limited coverage of business logic and configuration weaknesses.',
      'One time assessments without continuous monitoring capabilities.',
      'Minimal support for remediation validation and re-testing.'
    ],
    cyberaries: [
      'Combines automated scanning with expert manual validation and analysis.',
      'Eliminates false positives through thorough verification processes.',
      'Delivers actionable, context-aware remediation guidance tailored to your environment.',
      'Comprehensive coverage including technical, configuration, and logic flaws.',
      'Continuous monitoring with scheduled scans and real time dashboards.',
      'Complete support from identification through remediation and re-assessment.'
    ]
  };

  return (
    <div className="va-page">
      {/* Hero Section */}
      <section className="va-hero-section">
        <div
          className="va-hero-bg-layer"
          style={{ backgroundImage: `url(${heroBgImage})` }}
        />
        <div className="va-hero-overlay" />
        <div className="va-hero-grid-pattern" />
        {/* Floating particles */}
        <div className="va-hero-particles" aria-hidden="true">
          {Array.from({ length: 7 }).map((_, i) => (
            <span key={i} className="va-hero-particle" style={{ '--i': i }} />
          ))}
        </div>
        <div className="va-container">
          <div className="va-hero-content">
            <div className="va-hero-badge">
                  <Landmark size={14} className="va-badge-icon" />
                  <span className="va-badge-text">
                    OFFENSIVE SECURITY <span className="va-badge-divider">/</span> ADVANCED TESTING
                  </span>
                </div>
            <h1 className="va-hero-title">
              <span className="va-text-gradient">
                VULNERABILITY ASSESSMENT <br />
                &amp; RISK MANAGEMENT
              </span>
            </h1>
            <p className="va-hero-description">
              A <strong>Vulnerability Assessment (VA)</strong> is a systematic process for identifying, analyzing, and prioritizing
              security weaknesses across your IT infrastructure, applications, and networks.
            </p>
            <p className="va-hero-description">
              We help organizations proactively detect <strong>potential threats before attackers can exploit them</strong>, ensuring
              comprehensive security coverage and continuous protection.
            </p>
            <div className="va-hero-actions">
              <button
                className="va-btn va-btn-primary"
                type="button"
                onClick={() => navigate('/contact')}
              >
                Request a Vulnerability Audit
                <ArrowRight size={16} className="va-btn-arrow" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Service Overview Section */}
      <section className="va-service-overview">
        <div className="va-container">
          <div className="va-overview-grid">
            <ScrollReveal direction="left" delay={100}>
              <div className="va-overview-content">
                <p className="va-overview-text">
                  At <strong>Cyberaries</strong>, we deliver comprehensive <strong>Vulnerability Assessment services</strong> using
                  advanced tools, threat intelligence, and manual verification techniques to uncover both technical and
                  configuration level gaps.
                </p>
                <p className="va-overview-text">
                  Through a structured and risk based approach, we help organizations maintain secure <strong>visibility, compliance,
                  and resilience</strong>, ensuring every component of your environment is protected against emerging threats.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal direction="right" delay={200}>
              <div className="va-overview-visual">
                <img
                  src={overviewImage}
                  alt="Vulnerability Assessment"
                  className="va-overview-image"
                />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Our Approach Section - Modern Horizontal Timeline */}
      <section className="va-approach-section">
        <div className="va-grid-pattern" />
        <div className="va-container">
          <ScrollReveal direction="up" delay={0}>
            <div className="va-section-header va-text-center">
              <p className="va-eyebrow">Our Approach</p>
              <h2 className="va-approach-title">A Continuous Risk Based Methodology</h2>
              <p className="va-approach-subtitle">
                A structured, security first process designed to scan, validate, categorize, and permanently mitigate vulnerabilities
                across your applications and infrastructure.
              </p>
            </div>
          </ScrollReveal>

          {/* Row 1: Steps 1–5 */}
          <ScrollReveal direction="up" delay={100}>
            <div className="va-method-row">
              {approachSteps.slice(0, 5).map((step, index) => (
                <div className="va-method-card" key={index}>
                  <div className="va-method-badge">{String(index + 1).padStart(2, '0')}</div>
                  {index < 4 && <span className="va-method-connector" />}
                  <div className="va-method-icon">{step.icon}</div>
                  <h3 className="va-method-title">{step.title}</h3>
                  <p className="va-method-description">{step.description}</p>
                </div>
              ))}
            </div>
          </ScrollReveal>

          {/* Row 2: Steps 6–9 */}
          <ScrollReveal direction="up" delay={200}>
            <div className="va-method-row">
              {approachSteps.slice(5, 9).map((step, index) => (
                <div className="va-method-card" key={index + 5}>
                  <div className="va-method-badge">{String(index + 6).padStart(2, '0')}</div>
                  {index < 3 && <span className="va-method-connector" />}
                  <div className="va-method-icon">{step.icon}</div>
                  <h3 className="va-method-title">{step.title}</h3>
                  <p className="va-method-description">{step.description}</p>
                </div>
              ))}
            </div>
          </ScrollReveal>

          {/* Footer banner */}
          <ScrollReveal direction="up" delay={200}>
            <div className="va-method-footer">
              <div className="va-method-footer-icon">
                <Shield size={22} />
              </div>
              <p className="va-method-footer-text">
                Our vulnerability assessment methodology aligns with international security frameworks including OWASP, NIST, CIS Controls, and regulatory compliance standards.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="va-why-choose">
        <div className="va-container">
          <ScrollReveal direction="up" delay={0}>
            <div className="va-section-header va-text-center">
              <p className="va-why-eyebrow">Why CyberAries</p>
              <h2 className="va-section-title">Why Choose Cyberaries?</h2>
            </div>
          </ScrollReveal>

          <div className="va-why-choose-grid">
            {whyChooseItems.map((item, index) => (
              <ScrollReveal key={index} direction="up" delay={index * 150}>
                <div className="va-why-choose-card">
                  <div className="va-why-choose-card-header">
                    <div className="va-why-choose-icon-box">{item.icon}</div>
                    <h3 className="va-why-choose-title">{item.title}</h3>
                  </div>
                  <p className="va-why-choose-description">{item.description}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Difference Section */}
      <section className="va-cyberaries-difference">
        <div className="va-container">
          <ScrollReveal animation="fade-up">
            <h2 className="va-difference-title">
              THE <span className="va-highlight-red">CYBERARIES</span>{' '}
              ADVANTAGE
            </h2>
          </ScrollReveal>

          <div className="va-comparison-grid">
            <ScrollReveal animation="fade-right" delay={100}>
              <div className="va-comparison-column va-traditional">
                <h3 className="va-comparison-heading">Traditional VA Approach</h3>
                <ul className="va-comparison-list">
                  {difference.traditional.map((item, idx) => (
                    <li className="va-comparison-item" key={idx}>
                      <span className="va-bullet va-bullet-cross">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>

            <ScrollReveal animation="fade-left" delay={200}>
              <div className="va-comparison-column va-cyberaries-col">
                <h3 className="va-comparison-heading va-cyberaries-heading">
                  Cyberaries Vulnerability Assessment
                </h3>
                <ul className="va-comparison-list">
                  {difference.cyberaries.map((item, idx) => (
                    <li className="va-comparison-item" key={idx}>
                      <span className="va-bullet va-bullet-check">
                        <CheckCircle size={18} className="va-check-icon" />
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="va-cta-section" style={{ backgroundImage: `url(${ctaBgImage})` }}>
        <div className="va-cta-grid-pattern" />
        <div className="va-container">
          <ScrollReveal direction="up" delay={0}>
            <div className="va-cta-grid">
              <div className="va-cta-content">
                <h2 className="va-cta-title">
                  <span className="va-cta-text-gradient">Ready to Identify and Fix Vulnerabilities?</span>
                </h2>
                <p className="va-cta-description">
                  Strengthen your security posture before vulnerabilities become breaches.
                </p>
                <button
                  className="va-btn va-btn-primary va-btn-large"
                  type="button"
                  onClick={() => navigate('/contact')}
                >
                  Contact Us Today
                  <ArrowRight size={18} className="va-btn-arrow" />
                </button>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};

export default VulnerabilityAssessment;